import { useState } from 'react';

import {
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Input,
  InputGroup,
  InputRightElement,
  Stack,
  Text,
  Tooltip,
} from '@chakra-ui/react';

import { useForm } from 'react-hook-form';

import { yupResolver } from '@hookform/resolvers/yup';

import {
  ArrowLeftOutline,
  EyeOutline,
  EyeSlashOutline,
  CrossOutline,
  InfoCircleOutline,
} from '@aslanpay/portal-common-lib';

import SignUpSection from 'app/components/Authentication/SignUpSection';
import PasswordInfo from 'app/components/UI/PasswordInfo';

import { useAuthenticationStore } from 'app/store';

import { authenticationSchema } from 'app/utils/schemas';

import { AuthenticationStepProps, UserDetailsFormFields } from '../types';

function UserDetails({ onSetStep }: AuthenticationStepProps) {
  const authenticationData = useAuthenticationStore((state) => state.authenticationData);
  const setAuthenticationData = useAuthenticationStore((state) => state.setAuthenticationData);

  const {
    register,
    handleSubmit,
    watch,
    resetField,
    formState: { errors, isValid },
  } = useForm<UserDetailsFormFields>({
    mode: 'onChange',
    resolver: yupResolver(authenticationSchema.userDetails),
    defaultValues: {
      firstName: authenticationData.firstName,
      lastName: authenticationData.lastName,
      password: authenticationData.password,
      confirmPassword: authenticationData.confirmPassword,
    },
  });

  const [showNewPassword, setShowNewPassword] = useState<boolean>(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState<boolean>(false);

  const handleUserDetails = (data: UserDetailsFormFields) => {
    setAuthenticationData({ ...authenticationData, ...data });
    onSetStep('enableTwoFactor');
  };

  const watchFirstName = watch('firstName');
  const watchLastName = watch('lastName');

  const renderLoginInformationSection = () => (
    <SignUpSection title="Login information">
      <FormControl>
        <FormLabel>Email</FormLabel>
        <InputGroup>
          <Input
            name="email"
            variant="signUpEmail"
            isDisabled
            value={authenticationData.email}
            placeholder="Email not valid"
          />
        </InputGroup>
        <FormErrorMessage>{errors?.firstName?.message}</FormErrorMessage>
      </FormControl>
      <FormControl isInvalid={!!errors?.password?.message} isRequired>
        <Flex align="flex-start" gap="-6px">
          <FormLabel mr="6px">Create password</FormLabel>
          <Tooltip
            hasArrow
            arrowPadding={0}
            padding={0}
            background="brand.lightGray2"
            placement="top"
            label={<PasswordInfo />}
          >
            <Flex mt="1px">
              <InfoCircleOutline boxSize="15px" color="brand.darkPurple" cursor="pointer" />
            </Flex>
          </Tooltip>
        </Flex>
        <InputGroup>
          <Input
            type={showNewPassword ? 'text' : 'password'}
            placeholder="Create a password"
            {...register('password')}
          />
          <InputRightElement
            w="56px"
            h="full"
            cursor="pointer"
            onClick={() => setShowNewPassword((show) => !show)}
          >
            {showNewPassword ? (
              <EyeOutline color="brand.mutedPurple" boxSize="24px" />
            ) : (
              <EyeSlashOutline color="brand.mutedPurple" boxSize="24px" />
            )}
          </InputRightElement>
        </InputGroup>
        <FormErrorMessage>{errors?.password?.message}</FormErrorMessage>
      </FormControl>

      <FormControl isInvalid={!!errors?.confirmPassword?.message} isRequired>
        <FormLabel>Confirm password</FormLabel>
        <InputGroup>
          <Input
            type={showConfirmPassword ? 'text' : 'password'}
            placeholder="Confirm your password"
            {...register('confirmPassword')}
          />
          <InputRightElement
            w="56px"
            h="full"
            cursor="pointer"
            onClick={() => setShowConfirmPassword((show) => !show)}
          >
            {showConfirmPassword ? (
              <EyeOutline color="brand.mutedPurple" boxSize="24px" />
            ) : (
              <EyeSlashOutline color="brand.mutedPurple" boxSize="24px" />
            )}
          </InputRightElement>
        </InputGroup>
        <FormErrorMessage>{errors?.confirmPassword?.message}</FormErrorMessage>
      </FormControl>
    </SignUpSection>
  );

  const renderPersonalInformationSection = () => (
    <SignUpSection title="Personal information">
      <HStack spacing="20px" alignItems="flex-start">
        <FormControl isInvalid={!!errors?.firstName?.message} isRequired>
          <FormLabel>First name</FormLabel>
          <InputGroup>
            <Input type="text" placeholder="Enter first name" {...register('firstName')} />
            {watchFirstName && (
              <InputRightElement h="full" cursor="pointer" onClick={() => resetField('firstName')}>
                <CrossOutline color="brand.darkGray" boxSize="24px" />
              </InputRightElement>
            )}
          </InputGroup>
          <FormErrorMessage>{errors?.firstName?.message}</FormErrorMessage>
        </FormControl>

        <FormControl isInvalid={!!errors?.lastName?.message} isRequired>
          <FormLabel>Last name</FormLabel>
          <InputGroup>
            <Input type="text" placeholder="Enter last name" {...register('lastName')} />
            {watchLastName && (
              <InputRightElement h="full" cursor="pointer" onClick={() => resetField('lastName')}>
                <CrossOutline color="brand.darkGray" boxSize="24px" />
              </InputRightElement>
            )}
          </InputGroup>
          <FormErrorMessage>{errors?.lastName?.message}</FormErrorMessage>
        </FormControl>
      </HStack>
    </SignUpSection>
  );

  return (
    <Flex flexDir="column" mt="35px">
      <Button
        alignSelf="flex-start"
        size="sm"
        variant="link"
        leftIcon={<ArrowLeftOutline boxSize="20px" />}
        onClick={() => onSetStep('checkEmail')}
      >
        Back
      </Button>
      <Text as="h2" textStyle="h2" color="brand.darkPurple" mt="25px" mb="35px">
        Enter your details
      </Text>
      <form onSubmit={handleSubmit(handleUserDetails)} noValidate>
        <Stack spacing="40px">
          {renderLoginInformationSection()}
          {renderPersonalInformationSection()}
        </Stack>
        <Text my="27px" color="system.error" textStyle="extraSmallMedium">
          * mandatory
        </Text>
        <Button w="full" size="lg" type="submit" isDisabled={!isValid}>
          Next
        </Button>
      </form>
    </Flex>
  );
}

export default UserDetails;
